import type { GridPosition } from '../rendering/types';

export type FeatureSummaryCharacter = 'solomon' | 'joey' | 'seto' | 'marik';
export type FeatureSummaryTone = 'small' | 'regular' | 'big' | 'jackpot';

export interface FeatureSummaryTier {
  readonly tone: FeatureSummaryTone;
  readonly character: FeatureSummaryCharacter;
  readonly eyebrow: string;
  readonly title: string;
  readonly copy: string;
  readonly countUpMs: number;
}

export interface PotOfGreedTimeline {
  readonly highlightAtMs: number;
  readonly flipAtMs: number;
  readonly transformAtMs: number;
  readonly completeAtMs: number;
}

export const FEATURE_SUMMARY_AUTO_CLOSE_MS = 10_000;
export const REPLAY_STEP_INTERVAL_MS = 2_500;

export function getFeatureSummaryTier(totalWinCents: number, betCents: number): FeatureSummaryTier {
  const multiple = betCents > 0 ? totalWinCents / betCents : 0;
  if (multiple >= 1_000) {
    return {
      tone: 'jackpot', character: 'marik', eyebrow: 'UNGLAUBLICH', title: 'Schattenreich-Legende',
      copy: 'Ein Gewinn, über den selbst die alten Grabkammern noch flüstern werden.', countUpMs: 3_200,
    };
  }
  if (multiple >= 300) {
    return {
      tone: 'jackpot', character: 'seto', eyebrow: 'LEGENDÄR', title: 'Pharaonischer Triumph',
      copy: 'Dieser Run verdient einen Platz an der Spitze des Tempelarchivs.', countUpMs: 2_800,
    };
  }
  if (multiple >= 100) {
    return {
      tone: 'big', character: 'joey', eyebrow: 'MEGA FEATURE', title: 'Duelanten-Jackpot',
      copy: 'Sticky Wilds und starke Linien haben das Duell entschieden.', countUpMs: 2_200,
    };
  }
  if (multiple >= 20) {
    return {
      tone: 'regular', character: 'solomon', eyebrow: 'BIG FEATURE', title: 'Das Herz der Karten',
      copy: 'Ein starkes Freispiel-Ergebnis für deinen nächsten Ranked-Versuch.', countUpMs: 1_700,
    };
  }
  return {
    tone: 'small', character: 'solomon', eyebrow: 'FEATURE BEENDET', title: 'Der Tempel ruht',
    copy: 'Dieses Duell war knapp. Die nächste Kartenfolge wartet bereits.', countUpMs: 1_200,
  };
}

export function createPotOfGreedTimeline(turbo: boolean, reducedMotion: boolean): PotOfGreedTimeline {
  if (reducedMotion) return { highlightAtMs: 0, flipAtMs: 0, transformAtMs: 0, completeAtMs: 80 };
  const scale = turbo ? 0.55 : 1;
  const delay = (value: number): number => Math.max(55, Math.round(value * scale));
  const highlightAtMs = delay(240);
  const flipAtMs = highlightAtMs + delay(120);
  const transformAtMs = flipAtMs + delay(170);
  return {
    highlightAtMs,
    flipAtMs,
    transformAtMs,
    completeAtMs: transformAtMs + delay(150),
  };
}

export interface FeatureOverlayPort {
  showIntro(awardedSpins: number): Promise<void>;
  showRetrigger(awardedSpins: number): Promise<void>;
  showPotOfGreed(position: GridPosition): Promise<void>;
  showSummary(totalWinCents: number, betCents: number): Promise<void>;
}
